import { useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Switch, Tooltip, message } from "antd";
import moment from "moment";
import { PlusOutlined, QuestionCircleOutlined, WarningOutlined } from "@ant-design/icons";
import YextApi, { yextErrorMessage, YextHolidayRule, YextSyncStatus } from "../../api/YextApi";
import "./BusinessHoursEditor.scss";

type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

interface TimeRange {
  // Stored as minutes from midnight (0–1439, or 1440 for end-of-day / 24h).
  start: number;
  end: number;
}

interface DayHours {
  open: boolean;
  // Open around the clock. When true the from/to fields are hidden and the day
  // is treated as a single 00:00–24:00 range on save.
  is24: boolean;
  ranges: TimeRange[];
}

const DAYS: { key: DayKey; label: string }[] = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const DEFAULT_RANGE: TimeRange = { start: 8 * 60, end: 21 * 60 };
const FULL_DAY: TimeRange = { start: 0, end: 24 * 60 };

function defaultHours(): Record<DayKey, DayHours> {
  const day = (open: boolean): DayHours => ({ open, is24: false, ranges: [{ ...DEFAULT_RANGE }] });
  return {
    mon: day(true),
    tue: day(true),
    wed: day(true),
    thu: day(true),
    fri: day(true),
    sat: day(true),
    sun: day(true),
  };
}

// Holiday rules repeat every year — the customer sets the behavior once and we resolve the
// exact dates. Grouped into the two lists the design calls out.
type HolidayMode = "regular" | "closed" | "custom";

interface Holiday {
  key: string;
  label: string;
  mode: HolidayMode;
  custom: TimeRange;
}

const MAJOR_HOLIDAYS: { key: string; label: string }[] = [
  { key: "new-years-day", label: "New Year's Day" },
  { key: "mlk-day", label: "MLK Day" },
  { key: "presidents-day", label: "Presidents Day" },
  { key: "memorial-day", label: "Memorial Day" },
  { key: "juneteenth", label: "Juneteenth" },
  { key: "independence-day", label: "Independence Day" },
  { key: "labor-day", label: "Labor Day" },
  { key: "thanksgiving", label: "Thanksgiving" },
  { key: "christmas-day", label: "Christmas Day" },
  { key: "new-years-eve", label: "New Year's Eve" },
];

const BIG_DINING_DAYS: { key: string; label: string }[] = [
  { key: "valentines-day", label: "Valentine's Day" },
  { key: "easter", label: "Easter" },
  { key: "mothers-day", label: "Mother's Day" },
  { key: "fathers-day", label: "Father's Day" },
  { key: "christmas-eve", label: "Christmas Eve" },
  { key: "super-bowl-sunday", label: "Super Bowl Sunday" },
];

function makeHolidays(defs: { key: string; label: string }[]): Holiday[] {
  return defs.map((d) => ({ ...d, mode: "regular" as HolidayMode, custom: { ...DEFAULT_RANGE } }));
}

// Overlay stored holiday decisions (matched by key) onto a default group, keeping label + order.
function applyStoredHolidays(defaults: Holiday[], stored: YextHolidayRule[]): Holiday[] {
  const byKey = new Map(stored.map((r) => [r.key, r]));
  return defaults.map((h) => {
    const r = byKey.get(h.key);
    if (!r) return h;
    const mode = (r.mode ?? "regular") as HolidayMode;
    const custom =
      mode === "custom" && r.start != null && r.end != null ? { start: r.start, end: r.end } : h.custom;
    return { ...h, mode, custom };
  });
}

interface Props {
  locationId?: number;
  syncStatus?: YextSyncStatus | null;
  onSyncChange?: () => void;
}

export function BusinessHoursEditor({ locationId, syncStatus, onSyncChange }: Props) {
  const [hours, setHours] = useState<Record<DayKey, DayHours>>(defaultHours);
  const [majorHolidays, setMajorHolidays] = useState<Holiday[]>(() => makeHolidays(MAJOR_HOLIDAYS));
  const [bigDiningDays, setBigDiningDays] = useState<Holiday[]>(() => makeHolidays(BIG_DINING_DAYS));
  const [closeTempOpen, setCloseTempOpen] = useState(false);
  // Default the temporary-closure window to today through three days out.
  const [closeFrom, setCloseFrom] = useState<moment.Moment | null>(() => moment());
  const [closeThrough, setCloseThrough] = useState<moment.Moment | null>(() => moment().add(3, "days"));
  const [closureActive, setClosureActive] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [closingTemp, setClosingTemp] = useState(false);
  const [reopening, setReopening] = useState(false);
  // Autosave-draft bookkeeping: the last draft signature written to the DB. Edits past this trigger a
  // debounced draft save; publishing transmits and also advances it.
  const [savedDraftSig, setSavedDraftSig] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!locationId) return;
    YextApi.getProfile(locationId).then(({ data }) => {
      // Overlay stored days onto the defaults so any missing day still renders, and
      // detect the 00:00–24:00 range that means "open 24 hours".
      if (data.hours) {
        setHours((current) => {
          const stored = data.hours as Record<DayKey, Partial<DayHours>>;
          const next = { ...current };
          (Object.keys(next) as DayKey[]).forEach((key) => {
            const incoming = stored[key];
            if (!incoming) return;
            const ranges = incoming.ranges?.length ? incoming.ranges : next[key].ranges;
            const is24 = ranges.length === 1 && ranges[0].start === 0 && ranges[0].end === FULL_DAY.end;
            next[key] = {
              open: incoming.open ?? next[key].open,
              is24,
              ranges: is24 ? [{ ...DEFAULT_RANGE }] : ranges,
            };
          });
          return next;
        });
      }

      // Apply stored holiday decisions onto the default lists (matched by key).
      if (data.holidayHours?.length) {
        setMajorHolidays((cur) => applyStoredHolidays(cur, data.holidayHours!));
        setBigDiningDays((cur) => applyStoredHolidays(cur, data.holidayHours!));
      }

      if (data.temporaryClosure?.from && data.temporaryClosure.through) {
        setCloseFrom(moment(data.temporaryClosure.from));
        setCloseThrough(moment(data.temporaryClosure.through));
      }

      // Hydration done — allow autosave to begin (and baseline the saved-draft signature below).
      setHydrated(true);
    });
  }, [locationId]);

  useEffect(() => {
    if (syncStatus) setClosureActive(!!syncStatus.temporaryClosure);
  }, [syncStatus]);

  // Days marked "open 24 hours" collapse to a single full-day range on the wire.
  function buildHoursPayload() {
    return Object.fromEntries(
      (Object.keys(hours) as DayKey[]).map((key) => {
        const d = hours[key];
        return [key, { open: d.open, ranges: d.is24 ? [{ ...FULL_DAY }] : d.ranges }];
      })
    );
  }

  // Signature of the current draft (regular hours + holiday rules). Temporary closure has its own
  // publish action, so it's excluded here.
  const currentDraftSig = useMemo(
    () => JSON.stringify({ hours: buildHoursPayload(), holidayHours: holidayRules() }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hours, majorHolidays, bigDiningDays]
  );

  // Baseline the saved signature to the hydrated value once, so the initial load isn't seen as an edit.
  useEffect(() => {
    if (hydrated && savedDraftSig === null) setSavedDraftSig(currentDraftSig);
  }, [hydrated, currentDraftSig, savedDraftSig]);

  const draftDirty = savedDraftSig !== null && currentDraftSig !== savedDraftSig;

  // Autosave the draft (DB only, no Yext) shortly after edits settle.
  useEffect(() => {
    if (!locationId || !draftDirty) return;
    const t = setTimeout(() => {
      YextApi.saveDraft(locationId, { hours: buildHoursPayload(), holidayHours: holidayRules() })
        .then(() => {
          setSavedDraftSig(currentDraftSig);
          onSyncChange?.();
        })
        .catch(() => {
          // Draft autosave is best-effort; the change stays in the form and Publish still works.
        });
    }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftDirty, currentDraftSig, locationId]);

  // Publish is available when the draft is ahead of what's live (locally or per the backend).
  const canPublish = draftDirty || (syncStatus?.hasUnpublishedChanges ?? false);

  async function publishChanges() {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    setSaving(true);
    try {
      // Holiday rules ride along; the backend resolves them to concrete Yext dates. Temporary closure
      // is left untouched here (its own "Close and publish" owns it) — the backend merge preserves it.
      await YextApi.syncLocation(locationId, { hours: buildHoursPayload(), holidayHours: holidayRules() });
      setSavedDraftSig(currentDraftSig);
      message.success("Business hours published.");
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to publish business hours."));
    } finally {
      setSaving(false);
      // Refresh the pill either way — success flips it to "Synced", a failure surfaces "Sync failed".
      onSyncChange?.();
    }
  }

  // Flatten both holiday groups into the wire format (custom times only when the mode is custom).
  function holidayRules(): YextHolidayRule[] {
    const toRule = (h: Holiday, group: "major" | "big"): YextHolidayRule => ({
      key: h.key,
      label: h.label,
      group,
      mode: h.mode,
      ...(h.mode === "custom" ? { start: h.custom.start, end: h.custom.end } : {}),
    });
    return [
      ...majorHolidays.map((h) => toRule(h, "major")),
      ...bigDiningDays.map((h) => toRule(h, "big")),
    ];
  }

  async function closeAndPublish() {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    if (!closeFrom || !closeThrough) {
      message.error("Pick both a start and end date.");
      return;
    }
    if (closeThrough.isBefore(closeFrom, "day")) {
      message.error("The end date can't be before the start date.");
      return;
    }
    // Persist just the closure slice; the backend merge keeps regular + holiday hours intact and
    // expands this window into per-date closed entries on the Yext push.
    setClosingTemp(true);
    try {
      await YextApi.syncLocation(locationId, {
        temporaryClosure: {
          from: closeFrom.format("YYYY-MM-DD"),
          through: closeThrough.format("YYYY-MM-DD"),
        },
      });
      message.success("Temporary closure published.");
      setCloseTempOpen(false);
      setClosureActive(true);
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to save the temporary closure."));
    } finally {
      setClosingTemp(false);
      onSyncChange?.();
    }
  }

  async function reopenBusiness() {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    setReopening(true);
    try {
      await YextApi.reopen(locationId);
      message.success("Business reopened.");
      setClosureActive(false);
      setReopenOpen(false);
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to reopen the business."));
    } finally {
      setReopening(false);
      onSyncChange?.();
    }
  }

  function updateDay(day: DayKey, next: DayHours) {
    setHours((h) => ({ ...h, [day]: next }));
  }

  function toggleOpen(day: DayKey, open: boolean) {
    updateDay(day, { ...hours[day], open });
  }

  function toggle24(day: DayKey) {
    updateDay(day, { ...hours[day], is24: !hours[day].is24 });
  }

  function updateRange(day: DayKey, idx: number, range: TimeRange) {
    const ranges = hours[day].ranges.map((r, i) => (i === idx ? range : r));
    updateDay(day, { ...hours[day], ranges });
  }

  // A day supports at most one extra split (two intervals total).
  function addRange(day: DayKey) {
    if (hours[day].ranges.length >= 2) return;
    const ranges = [...hours[day].ranges, { ...DEFAULT_RANGE }];
    updateDay(day, { ...hours[day], is24: false, ranges });
  }

  function removeSplit(day: DayKey) {
    updateDay(day, { ...hours[day], ranges: [hours[day].ranges[0]] });
  }

  function setHolidayMode(list: "major" | "big", key: string, mode: HolidayMode) {
    const setter = list === "major" ? setMajorHolidays : setBigDiningDays;
    setter((hs) => hs.map((h) => (h.key === key ? { ...h, mode } : h)));
  }

  function setHolidayCustom(list: "major" | "big", key: string, custom: TimeRange) {
    const setter = list === "major" ? setMajorHolidays : setBigDiningDays;
    setter((hs) => hs.map((h) => (h.key === key ? { ...h, custom } : h)));
  }

  return (
    <section className="business-hours-editor">
      <div className="bh-publish-row">
        <Button
          className="bh-publish-btn"
          size="large"
          onClick={publishChanges}
          loading={saving}
          disabled={!canPublish}
        >
          Publish Changes
        </Button>
      </div>

      <div className="bh-section-head">
        <h3>Regular Hours</h3>
        {closureActive ? (
          <button
            type="button"
            className="bh-close-temp-link"
            onClick={() => setReopenOpen((v) => !v)}
          >
            Reopen business
          </button>
        ) : (
          <button
            type="button"
            className="bh-close-temp-link"
            onClick={() => setCloseTempOpen((v) => !v)}
          >
            Close temporarily
          </button>
        )}
      </div>

      {closureActive && reopenOpen && (
        <div className="bh-reopen-panel">
          <WarningOutlined className="bh-reopen-icon" />
          <div className="bh-reopen-text">
            <strong>This business is marked temporarily closed</strong>
            <p>All connected listings show as temporarily closed. Regular hours will be restored when you reopen.</p>
          </div>
          <Button className="bh-reopen-btn" onClick={reopenBusiness} loading={reopening}>
            Reopen business
          </Button>
        </div>
      )}

      {!closureActive && closeTempOpen && (
        <div className="bh-close-temp-panel">
          <span className="bh-close-temp-label">Closed from</span>
          <DatePicker
            className="bh-date-input"
            size="large"
            format="MM/DD/YYYY"
            allowClear={false}
            value={closeFrom}
            onChange={setCloseFrom}
          />
          <span className="bh-close-temp-label">through</span>
          <DatePicker
            className="bh-date-input"
            size="large"
            format="MM/DD/YYYY"
            allowClear={false}
            value={closeThrough}
            onChange={setCloseThrough}
          />
          <Button className="bh-close-publish-btn" onClick={closeAndPublish} loading={closingTemp}>
            Close and publish
          </Button>
        </div>
      )}

      <div className={`bh-rows${closureActive ? " bh-rows-closed" : ""}`}>
        {DAYS.map(({ key, label }) => (
          <DayRow
            key={key}
            label={label}
            day={hours[key]}
            disabled={closureActive}
            onToggleOpen={(open) => toggleOpen(key, open)}
            onToggle24={() => toggle24(key)}
            onChangeRange={(idx, range) => updateRange(key, idx, range)}
            onAddRange={() => addRange(key)}
            onRemoveSplit={() => removeSplit(key)}
          />
        ))}
      </div>

      <hr className="bh-divider" />

      <div className="bh-holidays">
        <div className="bh-holidays-head">
          <h3>Holiday Hours</h3>
          <Tooltip title="Confirmed holiday hours publish everywhere your listing lives (Google, Apple, Yelp, Facebook, Bing)">
            <QuestionCircleOutlined className="bh-help-icon" />
          </Tooltip>
        </div>
        <p className="bh-holidays-sub">
          Set each rule once — we handle the exact dates every year and check in two weeks before each
          holiday.
        </p>

        <h4 className="bh-group-title">Major Holidays</h4>
        <div className="bh-holiday-rows">
          {majorHolidays.map((h) => (
            <HolidayRow
              key={h.key}
              holiday={h}
              onSetMode={(mode) => setHolidayMode("major", h.key, mode)}
              onChangeCustom={(range) => setHolidayCustom("major", h.key, range)}
            />
          ))}
        </div>

        <hr className="bh-divider" />

        <h4 className="bh-group-title">Big Dining Days</h4>
        <div className="bh-holiday-rows">
          {bigDiningDays.map((h) => (
            <HolidayRow
              key={h.key}
              holiday={h}
              onSetMode={(mode) => setHolidayMode("big", h.key, mode)}
              onChangeCustom={(range) => setHolidayCustom("big", h.key, range)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

interface DayRowProps {
  label: string;
  day: DayHours;
  disabled?: boolean;
  onToggleOpen: (open: boolean) => void;
  onToggle24: () => void;
  onChangeRange: (idx: number, range: TimeRange) => void;
  onAddRange: () => void;
  onRemoveSplit: () => void;
}

function DayRow({
  label,
  day,
  disabled,
  onToggleOpen,
  onToggle24,
  onChangeRange,
  onAddRange,
  onRemoveSplit,
}: DayRowProps) {
  const hasSplit = day.ranges.length > 1;
  return (
    <div className={`bh-row${day.open ? "" : " closed"}`}>
      <div className="bh-toggle-cell">
        <Switch checked={day.open} disabled={disabled} onChange={onToggleOpen} />
        <span className="bh-day-label">{label}</span>
      </div>

      {day.open ? (
        <>
          <div className="bh-ranges">
            {day.is24 ? (
              <div className="bh-range bh-range-24">
                <span className="bh-24-note">Open 24 hours</span>
              </div>
            ) : (
              day.ranges.map((range, idx) => (
                <div className="bh-range" key={idx}>
                  <TimeField
                    value={range.start}
                    disabled={disabled}
                    onChange={(v) => onChangeRange(idx, { ...range, start: v })}
                  />
                  <span className="bh-dash">to</span>
                  <TimeField
                    value={range.end}
                    disabled={disabled}
                    onChange={(v) => onChangeRange(idx, { ...range, end: v })}
                  />
                </div>
              ))
            )}
          </div>

          <div className="bh-row-actions">
            <button
              type="button"
              className={`bh-pill-btn${day.is24 ? " active" : ""}`}
              disabled={disabled}
              onClick={onToggle24}
            >
              24 hr
            </button>
            {hasSplit ? (
              <button type="button" className="bh-pill-btn" disabled={disabled} onClick={onRemoveSplit}>
                Remove split
              </button>
            ) : (
              <button type="button" className="bh-pill-btn" disabled={disabled} onClick={onAddRange}>
                <PlusOutlined /> Add hours
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="bh-ranges bh-ranges-disabled" aria-hidden />
          <div className="bh-row-actions" aria-hidden />
        </>
      )}
    </div>
  );
}

interface HolidayRowProps {
  holiday: Holiday;
  onSetMode: (mode: HolidayMode) => void;
  onChangeCustom: (range: TimeRange) => void;
}

const HOLIDAY_MODES: { key: HolidayMode; label: string }[] = [
  { key: "regular", label: "Regular" },
  { key: "closed", label: "Closed" },
  { key: "custom", label: "Custom" },
];

function HolidayRow({ holiday, onSetMode, onChangeCustom }: HolidayRowProps) {
  return (
    <div className="bh-holiday-row">
      <div className="bh-holiday-main">
        <span className="bh-holiday-label">{holiday.label}</span>
        <div className="bh-segmented" role="group" aria-label={holiday.label}>
          {HOLIDAY_MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              className={`bh-segment${holiday.mode === m.key ? " active" : ""}`}
              onClick={() => onSetMode(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {holiday.mode === "custom" && (
        <div className="bh-holiday-custom">
          <TimeField
            value={holiday.custom.start}
            onChange={(v) => onChangeCustom({ ...holiday.custom, start: v })}
          />
          <span className="bh-dash">to</span>
          <TimeField
            value={holiday.custom.end}
            onChange={(v) => onChangeCustom({ ...holiday.custom, end: v })}
          />
          <span className="bh-every-year">every year</span>
        </div>
      )}
    </div>
  );
}

interface TimeFieldProps {
  value: number; // minutes from midnight
  disabled?: boolean;
  onChange: (value: number) => void;
}

function TimeField({ value, disabled, onChange }: TimeFieldProps) {
  const display = formatTime(value);

  // Local editable text so the user can type intermediate values before they parse. We commit to
  // the parent on blur, and re-sync from the formatted value whenever it changes externally.
  const [text, setText] = useState(display);
  useEffect(() => {
    setText(display);
  }, [display]);

  function commit() {
    const parsed = parseTime(text, value);
    if (parsed == null) {
      setText(display);
      return;
    }
    onChange(parsed);
    setText(formatTime(parsed));
  }

  return (
    <input
      type="text"
      className="bh-time-input"
      disabled={disabled}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      spellCheck={false}
    />
  );
}

// Format minutes-from-midnight as "8:00 AM". 1440 (end of day) renders as 12:00 AM.
function formatTime(min: number): string {
  const h24 = Math.floor(min / 60) % 24;
  const mi = min % 60;
  const pm = h24 >= 12;
  const h12raw = h24 % 12;
  const h12 = h12raw === 0 ? 12 : h12raw;
  return `${h12}:${pad(mi)} ${pm ? "PM" : "AM"}`;
}

// Parse lenient time text ("8", "8:30", "8:30 pm", "8 AM") to minutes. When no meridiem is typed we
// keep the field's current AM/PM. Returns null on unparseable input so the caller can revert.
function parseTime(text: string, current: number): number | null {
  const m = text.trim().match(/^(\d{1,2})(?::(\d{1,2}))?\s*([ap])\.?\s*m?\.?$/i) ||
    text.trim().match(/^(\d{1,2})(?::(\d{1,2}))?()$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mi = m[2] != null && m[2] !== "" ? parseInt(m[2], 10) : 0;
  if (isNaN(h) || isNaN(mi) || mi < 0 || mi > 59) return null;
  const meridiem = m[3] ? m[3].toLowerCase() : null;
  if (meridiem) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (meridiem === "p" ? 12 : 0);
  } else if (h >= 13 && h <= 23) {
    // Explicit 24-hour input.
  } else if (h >= 0 && h <= 12) {
    // Ambiguous — preserve the field's current period.
    const curPm = Math.floor(current / 60) % 24 >= 12;
    h = (h % 12) + (curPm ? 12 : 0);
  } else {
    return null;
  }
  if (h > 23) return null;
  return h * 60 + mi;
}

function pad(n: number) {
  return n < 10 ? `0${n}` : `${n}`;
}
